import { supabase } from '../utilities/supabase';
import BaseModel from './BaseModel';
import { PENSION_TYPE_KEYS } from '../utilities/pensionTypes';

// getByAuthIdLean's shared request (see there).
const AUTH_PROFILE_REUSE_MS = 3000;
let authProfileRequest = null;

// ---- Users list text boxes (used only by UserProfile.getAllByFunction) ----

// Turn a text-box value into clean search terms. The value arrives either as typed text
// ("Smith, Jones") or already split by the URL parser (["Smith", " Jones"]); treat both the same.
const searchTerms = value =>
  (Array.isArray(value) ? value : [value])
    .flatMap(v => (v === null || v === undefined ? [] : String(v).split(',')))
    .map(v => v.trim())
    .filter(Boolean);

// True when the operator actually typed something into the box (even if it is only punctuation).
// Arrays are joined with ',' so a lone ',' (URL shape ['', '']) counts as typed, like the text shape.
const hasTypedText = filter =>
  !!filter &&
  (Array.isArray(filter.value) ? filter.value.join(',') : String(filter.value ?? '')).trim() !== '';

// ID box: one or more numbers, e.g. "200534", "#200534" or "200534, 200535".
const displayIdTerms = value =>
  (Array.isArray(value) ? value.join(',') : String(value ?? ''))
    .split(/[\s,]+/)
    .map(v => v.replace(/^#/, ''))
    .filter(v => /^\d{1,18}$/.test(v));

// Phone box: "07700 900123", "+44 7700 900123", "0044 7700 900123" and "7700900123" all find the
// number however it is stored ("+447700900123", "+44 7700 900123", "07700900123").
// Same UK rules as public.global_search. Always a SUPERSET of the old contains-search: the typed
// digits also match anywhere in the number (so a trailing fragment such as "012345" still works).
// Returns a regex for PostgREST "match", or null when the text has no digits (then the old
// contains-search is used).
const phoneSearchPattern = value => {
  const text = String(value ?? '').trim();
  const digits = text.replace(/\D/g, '');
  if (!digits) return null;
  const international =
    /^(\+|00)\s*\(?\s*44/.test(text) || (digits.startsWith('44') && digits.length >= 11);
  const national = international
    ? digits.replace(/^(00)?44/, '').replace(/^0/, '')
    : digits.replace(/^0/, '');
  const between = '[^0-9]*'; // allow spaces/brackets/dashes between digits
  const anywhere = digits.split('').join(between);
  if (!national) return anywhere;
  const body = national.split('').join(between);
  // Typed with a leading 0 or +44: match from the start of the number, OR the digits anywhere.
  // ('|' has the lowest precedence in POSIX regex, so this is (^start...)|(anywhere) without
  // starting the value with a bracket.)
  return international || digits.startsWith('0')
    ? `^${between}(00)?(44)?${between}0?${between}${body}|${anywhere}`
    : body;
};

// National Insurance box: ignores case, spaces and punctuation and matches from the start, so
// "ab 12 34 56 c", "AB123456C" and "AB123456" all find AB123456C. Several can be comma-separated.
// Returns a regex for PostgREST "imatch", or null when nothing usable was typed.
const niSearchPattern = value => {
  const sep = '[^A-Za-z0-9]*';
  const terms = searchTerms(value)
    .map(t => t.replace(/[^A-Za-z0-9]/g, '').toUpperCase())
    .filter(Boolean);
  if (terms.length === 0) return null;
  return `^${sep}(${terms.map(t => t.split('').join(sep)).join('|')})`;
};

/**
 * Expand the compound Pension Values filter into column filters. Mutates the
 * `filters` object it is given (the table's per-request copy).
 * - Total row: unchanged.
 * - A ticked type always means "holds this type" (pension_<type> IS NOT NULL).
 *   pension_<type>_total is COALESCEd to 0 in the view and the RPC, so on its
 *   own it can never exclude people who don't hold the type.
 * - Transferable / Not Transferable with no amounts = that part is above £0.
 * - From / To amounts apply to the column for the chosen mode (unchanged).
 * - Unknown type keys (hand-edited links) are ignored instead of erroring.
 */
const expandPensionValueFilter = filters => {
  const config =
    typeof filters.pensionValueFilter.value === 'string'
      ? JSON.parse(filters.pensionValueFilter.value)
      : filters.pensionValueFilter.value;

  if (config) {
    // Total filter (unchanged)
    if (config.total && (config.total.from || config.total.to)) {
      const totalField =
        config.total.mode === 'good'
          ? 'pensions_value'
          : config.total.mode === 'bad'
            ? 'pensions_total_excluded'
            : 'pensions_total';
      if (config.total.from) {
        filters[`${totalField}`] = {
          ...(filters[totalField] || {}),
          operator: 'gte',
          value: Number(config.total.from)
        };
      }
      if (config.total.to) {
        filters[`${totalField}__lte`] = {
          operator: 'lte',
          value: Number(config.total.to),
          field: totalField
        };
      }
    }

    // Per-type filters
    if (config.types) {
      Object.entries(config.types).forEach(([typeKey, typeConfig]) => {
        if (!typeConfig?.enabled || !PENSION_TYPE_KEYS.includes(typeKey)) return;
        const baseField = `pension_${typeKey}`;
        const field =
          typeConfig.mode === 'good'
            ? baseField
            : typeConfig.mode === 'bad'
              ? `${baseField}_excluded`
              : `${baseField}_total`;
        // Must hold this type: pension_<type> is NULL when the user doesn't have it
        filters[`${baseField}__has`] = {
          operator: 'not',
          innerOperator: 'is',
          value: null,
          field: baseField
        };
        if (typeConfig.from || typeConfig.to) {
          if (typeConfig.from) {
            filters[field] = { operator: 'gte', value: Number(typeConfig.from) };
          }
          if (typeConfig.to) {
            filters[`${field}__lte`] = {
              operator: 'lte',
              value: Number(typeConfig.to),
              field: field
            };
          }
        } else if (typeConfig.mode === 'good' || typeConfig.mode === 'bad') {
          // Transferable / Not Transferable with no amounts = that part is above £0
          filters[field] = { operator: 'gt', value: 0 };
        }
      });
    }
  }
  delete filters.pensionValueFilter;
};

/**
 * Represents a User Profile in the system.
 * @extends BaseModel
 */
class UserProfile extends BaseModel {
  /** @type {string} The name of the database table for User Profiles */
  static table = 'user_profiles';
  // PORTAL_VIEW_V1: the top bar switch filters this list (audience column / computed field).
  static audienceFilter = true;
  /** @type {string} The SQL query to select User Profile data */
  static selectQuery = `*,
    title:user_profiles_title_fkey!inner(*),
    role:permission_groups!inner(*),
    segmentation:core_entity_types!user_profiles_segmentation_fkey!inner(*),
    status:core_entity_types!user_profiles_status_fkey!inner(*),
    pack:core_entity_types!user_profiles_pack_fkey!inner(*),
    signature:documents!user_profiles_signature_fkey(*),
    wet_signature:documents!user_profiles_wet_signature_fkey(*)`;


  /**
   * Creates an instance of UserProfile.
   * @param {Object} data - The user profile data.
   */
  constructor(data = {}) {
    super(data);
    this.auth = data.auth || null;
    this.firstName = data.first_name || null;
    this.lastName = data.last_name || null;
    this.fullName = data.full_name || null; // Added fullName from database
    this.email = data.email || null;
    this.phoneNumber = data.phone_number || null;
    this.title = data.title || null;
    // SECOND_BAND_V1 (read only): band 2 = came in through the second band.
    this.band = data.band ?? null;
    this.bandRoute = data.band_route || null;
    this.previousNames = data.previous_names || [];
    this.postcodes = data.postcodes || [];
    this.birthDate = data.birth_date || null;
    this.nationalInsurance = data.national_insurance || null;
    this.pack = data.pack
      ? typeof data.pack === 'object'
        ? { id: data.pack.id, type: data.pack.type }
        : { id: data.pack, type: data.pack_type || null }
      : null;
    this.packType = data.pack_type || null;
    this.segmentation = data.segmentation
      ? { id: data.segmentation.id, type: data.segmentation.type }
      : null;
    this.marketing = data.marketing || {
      post: true,
      text: true,
      email: true
    };
    this.tags = data.tags || [];
    this.profilePhoto = data.profile_photo || null;
    this.displayId = data.display_id || null;
    this.role = data.role || null;
    this.status = data.status || null;
    this.ownerId = data.owner || null;
    this.owner = null;
    this.followers = data.followers || [];
    this.advisors = data.advisors || [];
    this.signature = data.signature || null;
    // Handwritten signature lifted off a returned Wet Signature Form (documents row), and when
    // staff last asked for one. Both are set by database functions, never by a profile save.
    this.wetSignature = data.wet_signature || null;
    this.wetSignatureRequested = data.wet_signature_requested || null;
    // The day the wet signature counts from: the date signed on the form when it could be read,
    // else the day the scan was uploaded (or the day it was saved)
    this.wetSignatureValidFrom = data.wet_signature_valid_from || null;
    this.crmPreferences = data.crm_preferences || {};
    this.admin = data.admin ?? false;
    this.stats = data.stats ?? {};
    this.applicationCreated = data.application_created || null;
    this.applicationCompleted = data.application_completed || null;
    this.firstTraceProcessed = data.first_trace_processed || null;
    this.tracesCompleted = data.traces_completed || null;
    this.firstPackSent = data.first_pack_sent || null;
    this.firstPackReceived = data.first_pack_received || null;
    this.badges = data.badges || [];
    this.pipelines = data.pipelines || [];
    this.pensionContinueReason = data.pension_continue_reason || null;
    this.hasKnownPensions = data.has_known_pensions ?? false;
    this.consentGiven = data.consent_given ?? false;
    this.consentDate = data.consent_date || null;
    this.lastSignInAt = data.last_sign_in_at || null;
    this.isHmrc = data.is_hmrc ?? false;
    this.userType = data.user_type || null;
    this.traceCount = data.trace_count ?? 0;
    this.traceActiveCount = data.trace_active_count ?? 0;
    this.traceSentCount = data.trace_sent_count ?? 0;
    this.traceRespondedCount = data.trace_responded_count ?? 0;
    this.statusV2 = data.status_v2 || [];
    this.subStatusV2 = data.sub_status_v2 || [];
    this.funnelTracesNotComplete = data.funnel_traces_not_complete ?? false;
    this.notAllTracesSent = data.not_all_traces_sent ?? false;
    this.notAllProvidersResponded = data.not_all_providers_responded ?? false;
    // Saves send only changed fields (see BaseModel.rememberSaved).
    if (data.id) this.rememberSaved();
  }

  /**
   * Fetch owner profile data and set it on the instance
   */
  async fetchOwner() {
    if (!this.ownerId) return;

    try {
      const { data, error } = await supabase
        .from(this.constructor.table)
        .select('id, first_name, last_name, full_name, email')
        .eq('id', this.ownerId)
        .single();

      if (error) throw error;

      this.owner = {
        id: data.id,
        firstName: data.first_name,
        lastName: data.last_name,
        fullName: data.full_name,
        email: data.email
      };
    } catch (error) {
      console.error('[Error] Fetching owner profile:', error);
    }
  }

  /**
   * Converts the UserProfile instance to a database-friendly format.
   * @returns {Object} The user profile data ready for database operations.
   */
  toDatabase() {
    return {
      ...super.toDatabase(),
      auth: this.auth,
      first_name: this.firstName,
      last_name: this.lastName,
      email: this.email,
      phone_number: this.phoneNumber,
      title: this.title?.id || undefined,
      previous_names: this.previousNames,
      postcodes: this.postcodes,
      birth_date: this.birthDate,
      national_insurance: this.nationalInsurance,
      segmentation: this.segmentation?.id || undefined,
      marketing: this.marketing,
      tags: this.tags?.map(tag => tag?.id || tag) || [],
      profile_photo: this.profilePhoto,
      role: this.role?.id || undefined,
      status: this.status?.id ?? this.status?.id ?? 1,
      owner: this.owner?.id ?? this.ownerId ?? undefined,
      followers: this.followers,
      advisors: this.advisors || [],
      crm_preferences: this.crmPreferences,
      admin: this.admin,
      stats: this.stats,
      application_created: this.applicationCreated,
      application_completed: this.applicationCompleted,
      first_trace_processed: this.firstTraceProcessed,
      traces_completed: this.tracesCompleted,
      first_pack_sent: this.firstPackSent,
      first_pack_received: this.firstPackReceived,
      pack: this.pack?.id ?? undefined,
      pipelines: this.pipelines,
      status_v2: this.statusV2,
      sub_status_v2: this.subStatusV2
    };
  }

  /**
   * Get a record by its ID.
   * @param {string|number} id - The ID of the record to fetch.
   * @returns {Promise<BaseModel>} A promise that resolves to a new instance of the model.
   */
  static async getById(id) {
    const profile = await this.fetchOne('display_id', id, this.selectQuery);
    await profile.fetchOwner();
    return profile;
  }

  /**
   * Fetch a user profile by auth ID.
   * @param {string} id - The auth ID to fetch.
   * @returns {Promise<UserProfile>} A promise that resolves to a new instance of UserProfile.
   */
  static async getByAuthId(id) {
    return this.getAll({ auth: { value: id } });
  }

  /**
   * The signed-in user's profile for app startup: the same row and joins getByAuthId returns as
   * data[0], in one request (no exact count, no owner lookup; nothing reads the current user's
   * owner, and saving uses ownerId either way).
   *
   * On a page load both App's checkSession and the SIGNED_IN event Supabase fires for the
   * restored session ask for it, so calls for the same user share one request while it is in
   * flight and for a moment after it answers. A later call (e.g. SIGNED_IN when the tab regains
   * focus) fetches again; a failure is never handed to the next caller.
   * @param {string} authId - auth.users id.
   * @returns {Promise<UserProfile|null>} null when there is no profile. Rejects on failure.
   */
  static getByAuthIdLean(authId) {
    const shared = authProfileRequest;
    if (
      shared &&
      shared.model === this &&
      shared.authId === authId &&
      (shared.settledAt === null ||
        Date.now() - shared.settledAt < AUTH_PROFILE_REUSE_MS)
    ) {
      return shared.promise;
    }

    const request = { model: this, authId, promise: null, settledAt: null };
    request.promise = supabase
      .from(this.table)
      .select(this.selectQuery)
      .eq('auth', authId) // unique (user_profiles_auth_key)
      .maybeSingle()
      .then(({ data, error }) => {
        if (error) throw error;
        request.settledAt = Date.now();
        return data ? new this(data) : null;
      })
      .catch(error => {
        if (authProfileRequest === request) authProfileRequest = null;
        throw error;
      });
    authProfileRequest = request;
    return request.promise;
  }

  static async getByUniqueId(id) {
    const profile = await this.fetchOne('id', id);
    await profile.fetchOwner();
    return profile;
  }

  static async getAll(
    filters = {},
    page = 1,
    size = 10,
    sortBy = 'id',
    sortType = 'asc',
    count = true,
    signal = null
  ) {
    console.log(filters);
    // Remove statuses filter (used only for UI control) and handle substatuses
    if (filters.statuses) {
      delete filters.statuses;
    }
    if (filters.substatuses) {
      if (filters.tags) {
        // Merge substatuses values into existing tags filter
        const existing = Array.isArray(filters.tags.value) ? filters.tags.value : [filters.tags.value];
        const sub = Array.isArray(filters.substatuses.value) ? filters.substatuses.value : [filters.substatuses.value];
        filters.tags = {
          value: [...existing, ...sub],
          operator: 'cs',
          isOr: true,
          isArray: true,
        };
      } else {
        filters.tags = {
          value: filters.substatuses.value,
          label: 'Substatuses',
          operator: 'cs',
          isOr: true,
          isArray: true,
          friendlyValue: filters.substatuses.friendlyValue
        };
      }
      delete filters.substatuses;
    }

    // Handle case_statuses filter - maps to subtags
    if (filters.case_statuses) {
      if (filters.subtags) {
        // Merge case_statuses values into existing subtags filter
        const existing = Array.isArray(filters.subtags.value) ? filters.subtags.value : [filters.subtags.value];
        const sub = Array.isArray(filters.case_statuses.value) ? filters.case_statuses.value : [filters.case_statuses.value];
        filters.subtags = {
          value: [...existing, ...sub],
          operator: 'cs',
          isOr: true,
          isArray: true,
        };
      } else {
        filters.subtags = {
          value: filters.case_statuses.value,
          label: 'Case Status',
          operator: 'cs',
          isOr: true,
          isArray: true,
          friendlyValue: filters.case_statuses.friendlyValue
        };
      }
      delete filters.case_statuses;
    }

    // Handle noPipelines filter for users not in any pipelines
    if (filters.noPipelines) {
      filters.pipelines = {
        value: '{}',
        operator: 'eq',
        label: 'No Pipelines'
      };
      delete filters.noPipelines;
    }


    // Handle compound pension value filter (same helper as getAllByFunction)
    if (filters.pensionValueFilter) {
      expandPensionValueFilter(filters);
    }

    // Handle excludeHmrc toggle to switch traces outstanding field
    if (filters.excludeHmrc) {
      if (filters['stats->traces_outstanding']) {
        filters['stats->traces_outstanding_hmrc'] = filters['stats->traces_outstanding'];
        delete filters['stats->traces_outstanding'];
      }
      delete filters.excludeHmrc;
    }

    // Handle has_known_pensions filter - convert string 'true'/'false' to boolean
    if (filters.has_known_pensions) {
      if (filters.has_known_pensions.value === 'true' || filters.has_known_pensions.value === true) {
        filters.has_known_pensions.value = true;
      } else if (filters.has_known_pensions.value === 'false' || filters.has_known_pensions.value === false) {
        filters.has_known_pensions.value = false;
      }
    }

    console.log(filters);

    let query = super.getAll(
      filters,
      page,
      size,
      sortBy,
      sortType,
      count,
      signal
    );

    const result = await query;
    await Promise.all(result.data.map(profile => profile.fetchOwner()));
    return result;
  }

  /**
   * Get all records using a specific function with filtering, sorting, and pagination.
   * @param {Object} filters - The filters to apply.
   * @param {number} page - The page number.
   * @param {number} size - The page size.
   * @param {string} sortBy - The field to sort by.
   * @param {string} sortType - The sort type ('asc' or 'desc').
   * @returns {Promise<Object>} A promise that resolves to an object containing the data and pagination info.
   */
  static async getAllByFunction(
    filters = {},
    page = 1,
    size = 10,
    sortBy = 'id',
    sortType = 'asc',
    signal = null
  ) {
    try {
      // Statuses (tag ids 21/22/23/24/37), Substatuses (entity 1 / 62 tag ids)
      // and Case Status (entity 54) are stored in the tags / subtags arrays.
      // Each field becomes its OWN condition on that column: values inside one
      // field are OR-ed, different fields are AND-ed (PostgREST ANDs repeated
      // params). Nothing is merged into Tags/Subtags and Statuses is no longer
      // thrown away. Only the per-request copy is rewritten (EntityTable gives
      // the model its own copy) and the rewrite is idempotent (export re-uses
      // the same object for every page).
      //
      // OWNER DECISION (default below): no user carries the Archived tag (37);
      // its reasons (entity 62, e.g. Completed) sit on active users, so
      // 'Archived > reason' keeps filtering on the reason alone, as it does
      // today. Delete this `if` to make it strict (Archived AND reason = 0 users).
      const statusValue = Array.isArray(filters.statuses?.value)
        ? filters.statuses.value[0]
        : filters.statuses?.value;
      const cleanTagValues = raw =>
        (Array.isArray(raw) ? raw : String(raw ?? '').split(','))
          .map(v => String(v).trim())
          .filter(v => v !== '' && v !== 'undefined' && v !== 'null');
      if (
        String(statusValue ?? '') === '37' &&
        cleanTagValues(filters.substatuses?.value).length > 0
      ) {
        delete filters.statuses;
      }
      const arrayTagFields = {
        statuses: 'tags',
        substatuses: 'tags',
        case_statuses: 'subtags'
      };
      for (const [key, column] of Object.entries(arrayTagFields)) {
        if (!filters[key]) continue;
        const values = cleanTagValues(filters[key].value);
        if (values.length === 0) {
          delete filters[key];
          continue;
        }
        filters[key] = {
          value: values,
          operator: 'cs',
          isOr: true,
          isArray: true,
          field: column
        };
      }

      // 'Not in any pipelines' is its own condition on the pipelines column, so it
      // no longer overwrites a Pipelines selection (both together = 0 users).
      // Idempotent: export re-uses the same filter object for every page.
      if (filters.noPipelines) {
        filters.noPipelines = { value: '{}', operator: 'eq', field: 'pipelines' };
      }


      // Handle compound pension value filter
      if (filters.pensionValueFilter) {
        expandPensionValueFilter(filters);
      }

      // Handle excludeHmrc toggle to switch traces outstanding field
      if (filters.excludeHmrc) {
        if (filters['stats->traces_outstanding']) {
          filters['stats->traces_outstanding_hmrc'] = filters['stats->traces_outstanding'];
          delete filters['stats->traces_outstanding'];
        }
        delete filters.excludeHmrc;
      }

      // Handle has_known_pensions filter - convert string 'true'/'false' to boolean
      if (filters.has_known_pensions) {
        if (filters.has_known_pensions.value === 'true' || filters.has_known_pensions.value === true) {
          filters.has_known_pensions.value = true;
        } else if (filters.has_known_pensions.value === 'false' || filters.has_known_pensions.value === false) {
          filters.has_known_pensions.value = false;
        }
      }

      // ---- Text boxes ----
      // Handled here (not in BaseModel.filters) so typed text and URL lists behave the same and
      // spacing/case/format do not matter. The incoming filter objects are not changed; cleaned
      // copies go into `remainingFilters` instead.
      const { previous_names, postcodes, ...remainingFilters } = filters;
      const noResults = { data: [], count: 0, page, size, total: 0 };

      // First Name / Last Name / Email: ignore spaces before/after the text (copy-paste).
      ['first_name', 'last_name', 'email'].forEach(key => {
        const f = remainingFilters[key];
        if (f?.operator === 'ilike' && typeof f.value === 'string') {
          const trimmed = f.value.trim();
          if (trimmed) remainingFilters[key] = { ...f, value: trimmed };
          else delete remainingFilters[key];
        }
      });

      // ID: accept "#123" and comma lists; anything that is not an ID gives "no results", not an error.
      let displayIds = null;
      if (remainingFilters.display_id?.operator === 'eq' && hasTypedText(remainingFilters.display_id)) {
        displayIds = displayIdTerms(remainingFilters.display_id.value);
        delete remainingFilters.display_id;
        if (displayIds.length === 0) return noResults;
      }

      // Phone Number: 07... / +44... / spaced numbers all match.
      let phonePattern = null;
      if (remainingFilters.phone_number?.operator === 'ilike' && hasTypedText(remainingFilters.phone_number)) {
        phonePattern = phoneSearchPattern(remainingFilters.phone_number.value);
        if (phonePattern) {
          delete remainingFilters.phone_number;
        } else {
          remainingFilters.phone_number = {
            ...remainingFilters.phone_number,
            value: String(remainingFilters.phone_number.value).trim()
          };
        }
      }

      // National Insurance: case/space-insensitive prefix match, comma lists allowed.
      let niPattern = null;
      if (remainingFilters.national_insurance?.operator === 'in' && hasTypedText(remainingFilters.national_insurance)) {
        niPattern = niSearchPattern(remainingFilters.national_insurance.value);
        delete remainingFilters.national_insurance;
        if (!niPattern) return noResults;
      }

      // Previous Names / Postcodes: every comma-separated term is searched (the RPC matches ANY).
      // Postcodes: spaces and punctuation removed + uppercased; the RPC removes spaces from the
      // stored postcodes too.
      const previousNameTerms = searchTerms(previous_names?.value);
      const postcodeTerms = searchTerms(postcodes?.value)
        .map(pc => pc.replace(/[^A-Za-z0-9]/g, '').toUpperCase())
        .filter(Boolean);
      if (
        (hasTypedText(previous_names) && previousNameTerms.length === 0) ||
        (hasTypedText(postcodes) && postcodeTerms.length === 0)
      ) {
        return noResults; // e.g. just "," typed: show nothing rather than everyone
      }

      // Use the optimized view for normal queries, fall back to RPC only for array searches
      const needsArraySearch = previousNameTerms.length > 0 || postcodeTerms.length > 0;

      // get_user_profiles returns its own columns (no audience), so the top
      // bar switch cannot narrow a previous name / postcode search.
      if (needsArraySearch) delete remainingFilters.audience;

      let query;
      if (needsArraySearch) {
        // Fall back to RPC function for array ILIKE searches
        query = supabase.rpc(
          'get_user_profiles',
          {
            // Several terms are sent comma-separated; get_user_profiles (v3) matches any of them.
            p_previous_names: previousNameTerms.length ? previousNameTerms.join(',') : null,
            p_postcodes: postcodeTerms.length ? postcodeTerms.join(',') : null
          },
          { count: 'exact' }
        );
      } else {
        // Use the optimized view for all other queries
        query = supabase
          .from('user_profiles_view')
          .select('*', { count: 'exact' });
      }

      query = this.filters(query, remainingFilters);
      if (displayIds) query = query.in('display_id', displayIds);
      if (phonePattern) query = query.filter('phone_number', 'match', phonePattern);
      if (niPattern) query = query.filter('national_insurance', 'imatch', niPattern);
      // Old Title column sort key (no embed in this query) - sort by the title id instead of erroring.
      const safeSortBy = sortBy === 'title.type' ? 'title' : sortBy;
      query = this.sorting(query, safeSortBy, sortType, filters);
      query = this.range(query, page, size);

      if (signal) {
        query = query.abortSignal(signal);
      }

      const { data, error, count } = await query;

      if (error) throw error;

      return {
        data: data.map(item => new this(item)),
        count: count,
        page: page,
        size: size,
        total: Math.ceil(count / size)
      };
    } catch (error) {
      console.error(`[Error] Fetching All by Function ${this.name}: `, error);
      throw error;
    }
  }

  /**
   * Check if the user has a specific permission for a given entity.
   * @param {number} entityId - The ID of the entity to check permissions for.
   * @param {number} permissionFlag - The permission flag to check (e.g., 1 for read, 2 for write, 4 for update, 8 for delete).
   * @returns {boolean} True if the user has the specified permission, false otherwise.
   */
  hasPermission(entityId, permissionFlag) {
    // Check if the user has permissions for the given entity
    if (this.role?.permissions && this.role.permissions[entityId]) {
      // Perform bitwise AND operation to check if the permission flag is set
      return (
        (this.role.permissions[entityId] & permissionFlag) === permissionFlag
      );
    }
    return false; // No permissions found for the entity
  }

  /**
   * Save a new name to the previous names array and update the database.
   * @param {string} name - The name to add to the previous names.
   * @returns {Promise<void>} A promise that resolves when the name is saved.
   */
  async addPreviousName(name) {
    try {
      // Add the new name to the previous names array
      this.previousNames.push(name);
      // Update the database with the new previous names array
      await this.update({
        previous_names: this.previousNames
      });
    } catch (error) {
      console.error('Error saving previous names:', error);
    }
  }

  /**
   * Remove a previous name by index and update the database.
   * @param {number} index - The index of the name to remove from the previous names.
   * @returns {Promise<void>} A promise that resolves when the name is removed.
   */
  async removePreviousName(index) {
    try {
      // Remove the name at the specified index from the previous names array
      if (index >= 0 && index < this.previousNames.length) {
        this.previousNames.splice(index, 1);
        // Await the update to ensure the database is updated
        await this.update({
          previous_names: this.previousNames
        });
      } else {
        console.error('Index out of bounds');
      }
    } catch (error) {
      console.error('Error removing previous name:', error);
    }
  }

  /**
   * Update the UserProfile instance.
   * @param {Object} data - The data to update.
   * @returns {Promise<UserProfile>} A promise that resolves to the updated UserProfile instance.
   */
  async update(data) {
    const updated = await super.update(data);
    await updated.fetchOwner();
    return updated;
  }
}

export default UserProfile;
